import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import ContentHeader from "../../components/Admin/ContentHeader";
import CustomButton from "../../components/Admin/CustomButton/CustomButton";
import BranchQueueFilter from "../../components/Admin/BranchQueueFilter/BranchQueueFilter";
import { useAdminQueueIdsFilter } from "../../hooks/Admin/useAdminQueueIdsFilter";
import { useMyPermissions } from "../../hooks/useMyPermissions";
import {
  TicketNumberMode,
  useGetQueueScenarioSettings,
} from "../../hooks/Admin/QueueScenario/useGetQueueScenarioSettings";
import { usePutQueueScenarioSettings } from "../../hooks/Admin/QueueScenario/usePutQueueScenarioSettings";
import classes from "../MisSettingsPage/MisSettingsPage.module.scss";

const FORBIDDEN_LETTERS = new Set(["Ы", "Ъ", "Ь", "Й", "Ё"]);

const normalizeLetterInput = (value: string, fallback: string) => {
  const trimmed = value.trim().toUpperCase();
  if (!trimmed) return fallback;
  const letter = trimmed[0];
  if (!/^[А-Я]$/.test(letter) || FORBIDDEN_LETTERS.has(letter)) {
    return fallback;
  }
  return letter;
};

const QueueScenarioSettingsPage = () => {
  const { canEdit } = useMyPermissions();
  const canEditQueue = canEdit("SETTINGS_QUEUE");
  const { queueIdsFilter } = useAdminQueueIdsFilter();

  const [filterBranchIds, setFilterBranchIds] = useState<number[]>([]);
  const [filterQueueIds, setFilterQueueIds] = useState<number[]>([]);
  const hasSelectedQueue = filterQueueIds.some((id) => id > 0);
  const branchQueueId = hasSelectedQueue
    ? (filterQueueIds.find((id) => id > 0) ?? null)
    : null;

  const { data } = useGetQueueScenarioSettings(branchQueueId);
  const putSettings = usePutQueueScenarioSettings(branchQueueId);

  const [form, setForm] = useState({
    noShowTimeoutSeconds: 0,
    autoDistributeEnabled: false,
    autoDistributeDelaySeconds: 0,
    lcdRotationIntervalSeconds: 5,
    ticketNumberMode: "SERVICE_PREFIX" as TicketNumberMode,
    regularTicketPrefix: "А",
    benefitTicketPrefix: "Л",
    ticketNumberDigits: 3,
    ticketNumberPadZeros: true,
  });

  useEffect(() => {
    if (!data) {
      setForm({
        noShowTimeoutSeconds: 0,
        autoDistributeEnabled: false,
        autoDistributeDelaySeconds: 0,
        lcdRotationIntervalSeconds: 5,
        ticketNumberMode: "SERVICE_PREFIX",
        regularTicketPrefix: "А",
        benefitTicketPrefix: "Л",
        ticketNumberDigits: 3,
        ticketNumberPadZeros: true,
      });
      return;
    }
    setForm({
      noShowTimeoutSeconds: data.noShowTimeoutSeconds ?? 0,
      autoDistributeEnabled: Boolean(data.autoDistributeEnabled),
      autoDistributeDelaySeconds: data.autoDistributeDelaySeconds ?? 0,
      lcdRotationIntervalSeconds: data.lcdRotationIntervalSeconds ?? 5,
      ticketNumberMode: data.ticketNumberMode ?? "SERVICE_PREFIX",
      regularTicketPrefix: data.regularTicketPrefix ?? "А",
      benefitTicketPrefix: data.benefitTicketPrefix ?? "Л",
      ticketNumberDigits: data.ticketNumberDigits ?? 3,
      ticketNumberPadZeros:
        data.ticketNumberPadZeros == null
          ? true
          : Boolean(data.ticketNumberPadZeros),
    });
  }, [data]);

  const handleSave = () => {
    if (branchQueueId == null) return;
    putSettings.mutate({
      noShowTimeoutSeconds: form.noShowTimeoutSeconds,
      autoDistributeEnabled: form.autoDistributeEnabled,
      autoDistributeDelaySeconds: form.autoDistributeDelaySeconds,
      lcdRotationIntervalSeconds: form.lcdRotationIntervalSeconds,
      ticketNumberMode: form.ticketNumberMode,
      regularTicketPrefix: form.regularTicketPrefix,
      benefitTicketPrefix: form.benefitTicketPrefix,
      ticketNumberDigits: form.ticketNumberDigits,
      ticketNumberPadZeros: form.ticketNumberPadZeros,
    });
  };

  const examplePrefix =
    form.ticketNumberMode === "BENEFIT_FLAG"
      ? form.regularTicketPrefix || "А"
      : "А";
  const exampleNumber = form.ticketNumberPadZeros
    ? String(23).padStart(form.ticketNumberDigits, "0")
    : "23";
  const exampleTicket = `${examplePrefix}${exampleNumber}`;

  return (
    <div className={classes.container}>
      <ContentHeader
        name="Сценарий очереди"
        filterContent={
          <BranchQueueFilter
            selectedBranchIds={filterBranchIds}
            selectedQueueIds={filterQueueIds}
            onBranchChange={setFilterBranchIds}
            onQueueChange={setFilterQueueIds}
            singleQueue
            queueIdsFilter={queueIdsFilter}
          />
        }
      />

      {branchQueueId == null ? (
        <div className={classes.section}>
          <p className={classes.noQueueSelectedHint}>
            Выберите очередь в фильтре, чтобы настроить формат номера талона,
            таймаут неявки, автораспределение и ротацию LCD.
          </p>
        </div>
      ) : (
        <>
          <div className={classes.section}>
            <h5 className={classes.sectionTitle}>Формат номера талона</h5>
            <p className={classes.hint}>
              Номер: буква + цифры. Пример при текущих настройках:{" "}
              <strong>{exampleTicket}</strong>
            </p>

            <div className={classes.row}>
              <label className={`${classes.field} ${classes.fieldNarrow}`}>
                Число цифр (1–6)
                <Form.Control
                  type="number"
                  min={1}
                  max={6}
                  value={form.ticketNumberDigits}
                  disabled={!canEditQueue}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      ticketNumberDigits: Math.min(
                        6,
                        Math.max(1, Number(e.target.value) || 3),
                      ),
                    }))
                  }
                />
              </label>
            </div>
            <Form.Check
              type="switch"
              label="Дополнять нулями слева (А023 вместо А23)"
              checked={form.ticketNumberPadZeros}
              disabled={!canEditQueue}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  ticketNumberPadZeros: e.target.checked,
                }))
              }
            />
            <p className={classes.hint}>
              Если выключить дополнение нулями, номер будет без ведущих нулей
              (А1, А12, А123). Число цифр тогда влияет только на максимум
              счётчика (10ⁿ−1).
            </p>

            <div className={classes.modeGroup}>
              <div className={classes.modeTitle}>Режим буквы</div>
              <Form.Check
                type="radio"
                id="ticket-mode-service"
                name="ticketNumberMode"
                label="Префикс услуги (при льготе — буква льготы)"
                checked={form.ticketNumberMode === "SERVICE_PREFIX"}
                disabled={!canEditQueue}
                onChange={() =>
                  setForm((prev) => ({
                    ...prev,
                    ticketNumberMode: "SERVICE_PREFIX",
                  }))
                }
              />
              <Form.Check
                type="radio"
                id="ticket-mode-keep-service"
                name="ticketNumberMode"
                label="Префикс услуги (льгота не меняет букву)"
                checked={form.ticketNumberMode === "KEEP_SERVICE_PREFIX"}
                disabled={!canEditQueue}
                onChange={() =>
                  setForm((prev) => ({
                    ...prev,
                    ticketNumberMode: "KEEP_SERVICE_PREFIX",
                  }))
                }
              />
              <Form.Check
                type="radio"
                id="ticket-mode-benefit"
                name="ticketNumberMode"
                label="Признак льготы / без льготы (буквы задаются ниже)"
                checked={form.ticketNumberMode === "BENEFIT_FLAG"}
                disabled={!canEditQueue}
                onChange={() =>
                  setForm((prev) => ({
                    ...prev,
                    ticketNumberMode: "BENEFIT_FLAG",
                  }))
                }
              />
            </div>

            {(form.ticketNumberMode === "BENEFIT_FLAG" ||
              form.ticketNumberMode === "SERVICE_PREFIX") && (
              <div className={classes.row}>
                {form.ticketNumberMode === "BENEFIT_FLAG" && (
                  <label className={`${classes.field} ${classes.fieldNarrow}`}>
                    Буква без льготы
                    <Form.Control
                      maxLength={1}
                      value={form.regularTicketPrefix}
                      disabled={!canEditQueue}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          regularTicketPrefix: normalizeLetterInput(
                            e.target.value,
                            prev.regularTicketPrefix,
                          ),
                        }))
                      }
                    />
                  </label>
                )}
                <label className={`${classes.field} ${classes.fieldNarrow}`}>
                  Буква со льготой
                  <Form.Control
                    maxLength={1}
                    value={form.benefitTicketPrefix}
                    disabled={!canEditQueue}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        benefitTicketPrefix: normalizeLetterInput(
                          e.target.value,
                          prev.benefitTicketPrefix,
                        ),
                      }))
                    }
                  />
                </label>
              </div>
            )}
            {form.ticketNumberMode === "SERVICE_PREFIX" && (
              <p className={classes.hint}>
                Буква обычного талона берётся из настройки услуги. Для
                льготного талона — буква, заданная выше.
              </p>
            )}
            {form.ticketNumberMode === "KEEP_SERVICE_PREFIX" && (
              <p className={classes.hint}>
                Буква всегда берётся из настройки услуги — и для обычного, и для
                льготного талона.
              </p>
            )}
          </div>

          <div className={classes.section}>
            <h5 className={classes.sectionTitle}>Параметры очереди</h5>

            <Form.Check
              type="switch"
              label="Автораспределение талонов на свободных операторов"
              checked={form.autoDistributeEnabled}
              disabled={!canEditQueue}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  autoDistributeEnabled: e.target.checked,
                }))
              }
            />
            <p className={classes.hint}>
              Система сама вызывает следующего клиента на РМ со статусом
              «Свободен», если услуга доступна этому месту.
            </p>

            <div className={classes.row}>
              <label className={classes.field}>
                Секунд до автоматического вызова
                <Form.Control
                  type="number"
                  min={0}
                  max={3600}
                  value={form.autoDistributeDelaySeconds}
                  disabled={!canEditQueue || !form.autoDistributeEnabled}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      autoDistributeDelaySeconds: Math.max(
                        0,
                        Number(e.target.value) || 0,
                      ),
                    }))
                  }
                />
              </label>
            </div>
            <p className={classes.hint}>
              После перехода оператора в статус «Свободен» система ждёт
              указанное число секунд и только затем вызывает следующий талон.
              На кнопке «следующий» показывается обратный отсчёт. 0 — вызов
              сразу.
            </p>

            <div className={classes.row}>
              <label className={classes.field}>
                Таймаут неявки (сек), 0 — выключен
                <Form.Control
                  type="number"
                  min={0}
                  max={3600}
                  value={form.noShowTimeoutSeconds}
                  disabled={!canEditQueue || !form.autoDistributeEnabled}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      noShowTimeoutSeconds: Number(e.target.value) || 0,
                    }))
                  }
                />
              </label>
              <label className={classes.field}>
                Интервал ротации LCD (сек)
                <Form.Control
                  type="number"
                  min={1}
                  max={120}
                  value={form.lcdRotationIntervalSeconds}
                  disabled={!canEditQueue || !form.autoDistributeEnabled}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      lcdRotationIntervalSeconds: Math.max(
                        1,
                        Number(e.target.value) || 5,
                      ),
                    }))
                  }
                />
              </label>
            </div>
            <p className={classes.hint}>
              Работает только вместе с автораспределением: по истечении таймаута
              талон автоматически закрывается как «не пришёл», затем вызывается
              следующий. Без автораспределения неявку отмечает оператор сам. На
              кнопке «не пришел» показывается обратный отсчёт.
            </p>

            {canEditQueue && (
              <div className={classes.row}>
                <CustomButton
                  content={putSettings.isLoading ? "Сохранение..." : "Сохранить"}
                  styleType="active"
                  onClick={handleSave}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default QueueScenarioSettingsPage;
